// Tappable multi-layer pain-capture canvas (PAIN_TRACKING_PLAN.md Task 4.1).
//
// A SEPARATE component from BodyMap.tsx on purpose (hazard H2): BodyMap.tsx is
// consumed by TrainerPane.tsx and requires load / mode / windowDays props that
// pain has no values for. Widening its props would silently change Trainer's
// heat rendering. The duplicated SVG scaffolding here is correct — the two
// components encode unrelated decisions and share only the React-free region
// data they both import.
//
// Layers, bottom to top: silhouette outline, muscle regions (dimmed when a
// structure layer sits over them), the non-muscle structure layer, a heat tint
// (Map tab), the radiation polyline with an arrowhead, and the mark pins.
//
// Structure resolution (D-PAIN-12): the tapped path elements ARE the regions.
// On a tap we read data-* off every SVG path under the pointer via
// document.elementsFromPoint — that yields the muscle AND the joint under one
// tap with no point-in-polygon math, and the 100KB+ of path data never leaves
// the browser.

import { useId, useMemo, useRef, useState, type MouseEvent as ReactMouseEvent, type PointerEvent as ReactPointerEvent } from "react";
import type {
  AnatomicalMuscle,
  HeadacheLocation,
  PainBodyMap,
  PainArtworkVersion,
  PainMark,
  PainStructureKind,
  PainStructureRef,
  PainView,
} from "@medassist/shared";
import { LEGACY_PAIN_ARTWORK_VERSION, MUSCLE_META, PAIN_STRUCTURE_META, PAIN_VIEW_BOUNDS, isAnatomicalMuscle } from "@medassist/shared";
import { heatFill } from "./heat.js";
import { mirrorPath } from "./geometry.js";
import { painViewDataFor, type PainRegion } from "./painArtwork.js";

/** The draft a tap produces. Structurally `PainMark` plus the tapped region's
 *  `headacheLocation` (present only on the top-down / face panels, D-PAIN-2) so
 *  the caller can route the saved event to a `headache` without a lookup table. */
export type PainMarkDraft = Omit<PainMark, "structures"> & {
  structures: PainStructureRef[];
  headacheLocation?: HeadacheLocation;
};

interface HoverInfo {
  label: string;
  common: string;
  blurb: string;
  x: number;
  y: number;
}

const TOOLTIP_WIDTH = 240;
const TOOLTIP_MARGIN = 12;

/** Views that draw a single limb and mirror to the other side via mirrorPath
 *  (Canvas conventions) — never a second hand-authored copy. */
const LIMB_AXIS: Partial<Record<PainView, number>> = {
  "hand-palmar": 100,
  "hand-dorsal": 100,
  "foot-dorsal": 80,
  "foot-plantar": 80,
};

/** Per-kind tint for the structure layer when no heat map is supplied. */
const KIND_FILL: Record<PainStructureKind, string> = {
  muscle: "rgba(148,163,184,0.30)",
  joint: "rgba(59,130,246,0.34)",
  bone: "rgba(217,119,6,0.30)",
  nerve: "rgba(217,70,239,0.30)",
  dermatome: "rgba(16,185,129,0.26)",
  visceral: "rgba(244,63,94,0.28)",
  skin: "rgba(234,179,8,0.28)",
};

function flipSide(s: PainStructureRef["side"]): PainStructureRef["side"] {
  return s === "left" ? "right" : s === "right" ? "left" : "center";
}

function StructureTooltip({ label, common, blurb, x, y }: HoverInfo) {
  const viewportW = typeof window !== "undefined" ? window.innerWidth : 1024;
  const viewportH = typeof window !== "undefined" ? window.innerHeight : 768;
  const left = Math.min(Math.max(TOOLTIP_MARGIN, x + 16), viewportW - TOOLTIP_WIDTH - TOOLTIP_MARGIN);
  const top = Math.min(Math.max(TOOLTIP_MARGIN, y - 12), viewportH - TOOLTIP_MARGIN - 40);
  return (
    <div
      role="tooltip"
      className="pointer-events-none fixed z-50 rounded-xl border border-gray-200 bg-white p-3 text-xs shadow-lg dark:border-gray-700 dark:bg-gray-800"
      style={{ left, top, width: TOOLTIP_WIDTH }}
    >
      <p className="font-semibold text-gray-900 dark:text-gray-100">{label}</p>
      <p className="text-[11px] text-gray-500 dark:text-gray-400">{common}</p>
      <p className="mt-1.5 leading-snug text-gray-600 dark:text-gray-300">{blurb}</p>
    </div>
  );
}

export function PainMapCanvas({
  view,
  marks,
  radiation,
  artworkVersion = LEGACY_PAIN_ARTWORK_VERSION,
  heat,
  limbSide = "left",
  readOnly = false,
  maxHeightPx,
  onMark,
  onRadiationChange,
}: {
  view: PainView;
  marks: PainMark[];
  radiation?: PainBodyMap["radiation"];
  /** Missing historical versions are v1. Unknown/unavailable versions fail
   * closed instead of rendering coordinates on a newer drawing. */
  artworkVersion?: PainArtworkVersion | number | undefined;
  /** structureId / muscleId -> 0..1. Map tab only. */
  heat?: Map<string, number>;
  /** Hand / foot panels only; mirrors the single authored limb via mirrorPath. */
  limbSide?: "left" | "right";
  readOnly?: boolean;
  /** Caps rendered height (px); mirrors BodyMap.tsx's prop of the same name. */
  maxHeightPx?: number | null;
  onMark?: (mark: PainMarkDraft) => void;
  onRadiationChange?: (pts: { x: number; y: number }[]) => void;
}) {
  const data = painViewDataFor(artworkVersion, view);
  const svgRef = useRef<SVGSVGElement>(null);
  // Unique per instance so several canvases on one page (Sites/Map thumbnails)
  // don't collide on the arrowhead marker id.
  const arrowId = `pain-arrowhead-${useId().replace(/:/g, "")}`;
  const [hover, setHover] = useState<HoverInfo | null>(null);
  // Pointer-drag bookkeeping: a small move => a tap (place a mark); a larger
  // move with onRadiationChange wired => a radiation stroke.
  const dragRef = useRef<{ startX: number; startY: number; pts: { x: number; y: number }[]; moved: boolean } | null>(null);
  const [livePts, setLivePts] = useState<{ x: number; y: number }[] | null>(null);

  const axis = LIMB_AXIS[view];
  const mirror = limbSide === "right" && axis !== undefined;
  const radiationEnabled = !readOnly && !!onRadiationChange;

  const path = (d: string) => (mirror && axis !== undefined ? mirrorPath(d, axis) : d);
  const toDisplayX = (x: number) => (mirror && axis !== undefined ? 2 * axis - x : x);
  // The stored coordinate space is the authored (un-mirrored) one, so a site's
  // pins re-render correctly no matter which limb toggle is active.
  const toCanonicalX = (x: number) => (mirror && axis !== undefined ? 2 * axis - x : x);

  const outlines = useMemo(() => data?.outline.map(path) ?? [], [data?.outline, mirror, axis]);

  if (!data) {
    return (
      <div className="rounded-lg border border-amber-400 bg-amber-50 p-3 text-sm text-amber-950 dark:border-amber-700 dark:bg-amber-950/30 dark:text-amber-100" role="status">
        This pain location uses unsupported artwork version {String(artworkVersion)}. It is intentionally not shown on a different body map.
      </div>
    );
  }

  function svgPoint(clientX: number, clientY: number): { x: number; y: number } {
    const svg = svgRef.current;
    const ctm = svg?.getScreenCTM();
    if (!svg || !ctm) return { x: 0, y: 0 };
    const inv = ctm.inverse();
    // Clamp into the panel's own canvas: a tap on the very edge can map a
    // fraction outside the viewBox, and the API bounds-check (Task 4.3) rejects
    // an out-of-canvas mark.
    const b = PAIN_VIEW_BOUNDS[view];
    return {
      x: Math.min(b.width, Math.max(0, clientX * inv.a + clientY * inv.c + inv.e)),
      y: Math.min(b.height, Math.max(0, clientX * inv.b + clientY * inv.d + inv.f)),
    };
  }

  /** Collects every structure / muscle under the pointer plus the topmost
   *  region's headacheLocation (D-PAIN-12). */
  function resolveAt(clientX: number, clientY: number): { structures: PainStructureRef[]; headacheLocation?: HeadacheLocation } {
    const refs = new Map<string, PainStructureRef>();
    let headacheLocation: HeadacheLocation | undefined;
    const stack = typeof document !== "undefined" ? document.elementsFromPoint(clientX, clientY) : [];
    for (const el of stack) {
      if (!(el instanceof SVGElement)) continue;
      const structure = el.getAttribute("data-structure");
      const rawSide = (el.getAttribute("data-side") as PainStructureRef["side"] | null) ?? "center";
      const side = mirror ? flipSide(rawSide) : rawSide;
      if (structure) {
        const kind = (el.getAttribute("data-kind") as PainStructureKind | null) ?? "skin";
        if (!refs.has(structure)) refs.set(structure, { id: structure, kind, side });
        const hl = el.getAttribute("data-headache-location") as HeadacheLocation | null;
        if (hl && !headacheLocation) headacheLocation = hl;
        continue;
      }
      const muscle = el.getAttribute("data-muscle");
      if (muscle && !refs.has(muscle)) refs.set(muscle, { id: muscle, kind: "muscle", side });
    }
    return { structures: [...refs.values()], ...(headacheLocation ? { headacheLocation } : {}) };
  }

  function handlePointerDown(e: ReactPointerEvent<SVGSVGElement>) {
    if (readOnly) return;
    svgRef.current?.setPointerCapture?.(e.pointerId);
    const p = svgPoint(e.clientX, e.clientY);
    dragRef.current = { startX: e.clientX, startY: e.clientY, pts: [p], moved: false };
    if (radiationEnabled) setLivePts([p]);
  }

  function handlePointerMove(e: ReactPointerEvent<SVGSVGElement>) {
    const drag = dragRef.current;
    if (!drag) return;
    const dx = e.clientX - drag.startX;
    const dy = e.clientY - drag.startY;
    if (dx * dx + dy * dy > 16) drag.moved = true;
    if (radiationEnabled && drag.moved) {
      drag.pts.push(svgPoint(e.clientX, e.clientY));
      setLivePts([...drag.pts]);
    }
  }

  function handlePointerUp(e: ReactPointerEvent<SVGSVGElement>) {
    const drag = dragRef.current;
    dragRef.current = null;
    if (!drag || readOnly) {
      setLivePts(null);
      return;
    }
    if (radiationEnabled && drag.moved) {
      // A radiation stroke — store canonical (un-mirrored) coords.
      const pts = drag.pts.map((p) => ({ x: toCanonicalX(p.x), y: p.y }));
      onRadiationChange?.(pts);
      setLivePts(null);
      return;
    }
    setLivePts(null);
    if (!onMark) return;
    const p = svgPoint(e.clientX, e.clientY);
    const { structures, headacheLocation } = resolveAt(e.clientX, e.clientY);
    onMark({
      view,
      x: Math.round(toCanonicalX(p.x) * 100) / 100,
      y: Math.round(p.y * 100) / 100,
      structures,
      ...(headacheLocation ? { headacheLocation } : {}),
    });
  }

  function showStructureHover(e: ReactMouseEvent, region: PainRegion) {
    const meta = PAIN_STRUCTURE_META[region.structure];
    setHover({ label: meta.label, common: meta.common, blurb: meta.blurb, x: e.clientX, y: e.clientY });
  }
  function showMuscleHover(e: ReactMouseEvent, muscle: AnatomicalMuscle) {
    const meta = MUSCLE_META[muscle];
    setHover({ label: meta.label, common: meta.common, blurb: meta.blurb, x: e.clientX, y: e.clientY });
  }

  const dimMuscles = data.structures.length > 0;
  const muscleFill = (id: string): string => (heat ? heatFill(heat.get(id) ?? 0) : "var(--bodymap-outline)");
  const structureFill = (r: PainRegion): string => (heat ? heatFill(heat.get(r.structure) ?? 0) : KIND_FILL[r.kind]);

  const displayRadiation = livePts ?? (radiation && radiation.view === view ? radiation.points : null);

  return (
    <div className="relative w-full">
      <svg
        ref={svgRef}
        viewBox={data.viewBox}
        className="h-auto w-full max-h-[70vh]"
        style={{
          touchAction: "none",
          ...(maxHeightPx ? { maxHeight: maxHeightPx } : {}),
          ...(readOnly ? {} : { cursor: "crosshair" }),
        }}
        role="img"
        aria-label={`Pain map, ${view} view`}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
      >
        <defs>
          <marker id={arrowId} markerWidth="7" markerHeight="7" refX="5" refY="3.2" orient="auto">
            <path d="M0 0 L7 3.2 L0 6.4 Z" fill="var(--accent)" />
          </marker>
        </defs>

        {/* Silhouette — stroked so limbs/digits stay distinguishable. */}
        {outlines.map((d, i) => (
          <path
            key={`outline-${i}`}
            d={d}
            fill="var(--bodymap-outline)"
            stroke="var(--bodymap-stroke)"
            strokeWidth={0.8}
            strokeLinejoin="round"
            pointerEvents="none"
          />
        ))}

        {/* Muscle layer (front/back/side only) — dimmed under the structure layer. */}
        {data.muscles.map((region) => (
          <path
            key={region.id}
            d={region.d}
            data-muscle={region.muscle}
            data-side={region.side}
            fill={muscleFill(region.muscle)}
            stroke="var(--bodymap-stroke)"
            strokeWidth={0.5}
            opacity={dimMuscles && !heat ? 0.4 : 1}
            onMouseMove={(e) => isAnatomicalMuscle(region.muscle) && showMuscleHover(e, region.muscle)}
            onMouseEnter={(e) => isAnatomicalMuscle(region.muscle) && showMuscleHover(e, region.muscle)}
            onMouseLeave={() => setHover(null)}
          />
        ))}

        {/* Non-muscle structure layer. */}
        {data.structures.map((region) => (
          <path
            key={region.id}
            d={path(region.d)}
            data-id={region.id}
            data-structure={region.structure}
            data-kind={region.kind}
            data-side={region.side}
            data-headache-location={region.headacheLocation}
            fill={structureFill(region)}
            stroke="var(--accent)"
            strokeWidth={0.5}
            strokeOpacity={0.55}
            onMouseMove={(e) => showStructureHover(e, region)}
            onMouseEnter={(e) => showStructureHover(e, region)}
            onMouseLeave={() => setHover(null)}
          />
        ))}

        {/* Radiation path with an arrowhead. */}
        {displayRadiation && displayRadiation.length >= 2 && (
          <polyline
            points={displayRadiation.map((p) => `${toDisplayX(p.x)},${p.y}`).join(" ")}
            fill="none"
            stroke="var(--accent)"
            strokeWidth={2.5}
            strokeLinecap="round"
            strokeLinejoin="round"
            markerEnd={`url(#${arrowId})`}
            pointerEvents="none"
          />
        )}

        {/* Mark pins. */}
        {marks
          .filter((m) => m.view === view)
          .map((m, i) => {
            const cx = toDisplayX(m.x);
            const cy = m.y;
            return (
              <g key={`mark-${i}`} pointerEvents="none">
                <circle cx={cx} cy={cy} r={7} fill="none" stroke="var(--accent)" strokeWidth={2} />
                <line x1={cx - 11} y1={cy} x2={cx - 3} y2={cy} stroke="var(--accent)" strokeWidth={2} strokeLinecap="round" />
                <line x1={cx + 3} y1={cy} x2={cx + 11} y2={cy} stroke="var(--accent)" strokeWidth={2} strokeLinecap="round" />
                <line x1={cx} y1={cy - 11} x2={cx} y2={cy - 3} stroke="var(--accent)" strokeWidth={2} strokeLinecap="round" />
                <line x1={cx} y1={cy + 3} x2={cx} y2={cy + 11} stroke="var(--accent)" strokeWidth={2} strokeLinecap="round" />
                <circle cx={cx} cy={cy} r={1.8} fill="var(--accent)" />
              </g>
            );
          })}
      </svg>

      {hover && <StructureTooltip {...hover} />}
    </div>
  );
}
